import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle, Send, Copy, Search } from "lucide-react";
import AudiencePresets from "./AudiencePresets";
import { API, fmtDateTime } from "./utils";

/* ---------- Broadcast Modal ---------- */
export default function WebinarBroadcastModal({ webinar, onClose, token }) {
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [force, setForce] = useState(false);
  // Audience cohort selection — same pattern as the tech-invite modal.
  // Default matches the legacy backend behaviour so existing one-click
  // broadcasts (Leads + Kunden + Kontakte) keep working unchanged.
  const [audiences, setAudiences] = useState(
    () => new Set(["leads", "customers", "contacts"]),
  );
  const headers = { "X-Admin-Token": token };

  const toggleAudience = (key) => {
    setAudiences((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) next.add("leads"); // never allow empty
      return next;
    });
  };

  const runDry = async (forceFlag, audSet = audiences) => {
    setBusy(true);
    try {
      const audParam = Array.from(audSet).join(",") || "leads,customers,contacts";
      const url = `${API}/admin/webinars/${webinar.id}/broadcast?dry_run=true&audiences=${encodeURIComponent(audParam)}${forceFlag ? "&force=true" : ""}`;
      const { data } = await axios.post(url, {}, { headers });
      setDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Empfänger nicht ermitteln.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    runDry(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run dry-run whenever force toggle flips so candidate count is live.
  useEffect(() => {
    if (dryRun !== null) runDry(force, audiences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [force]);

  // Same for audience selection — keep the preview in sync.
  useEffect(() => {
    if (dryRun !== null) runDry(force, audiences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiences]);

  const doSend = async () => {
    const skipped = dryRun?.skipped_already_invited || 0;
    const confirmMsg = force
      ? `FORCE-MODUS: ${dryRun?.candidates_count} Empfänger inkl. derer, die schon eine andere Webinar-Einladung erhalten haben. Wirklich senden?`
      : `Wirklich an ${dryRun?.candidates_count} Empfänger versenden?${skipped ? `\n\n${skipped} Empfänger haben bereits eine Einladung — werden übersprungen.` : ""}`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const audParam = Array.from(audiences).join(",") || "leads,customers,contacts";
      const url = `${API}/admin/webinars/${webinar.id}/broadcast?audiences=${encodeURIComponent(audParam)}${force ? "&force=true" : ""}`;
      const { data } = await axios.post(url, {}, { headers });
      setResult(data);
      if (data.ok) toast.success(`✓ ${data.sent} Mails verschickt${data.failed ? `, ${data.failed} Fehler` : ""}.`);
      else toast.error(`Broadcast übersprungen: ${data.reason || "siehe Details"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="webinar-broadcast-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="font-display font-bold text-lg">Einladung versenden</h2>
            <p className="text-xs text-slate-500 mt-1">{webinar.title} · {fmtDateTime(webinar.scheduled_at)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        {busy && !dryRun && !result ? (
          <div className="py-10 text-center text-slate-400 flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
            {!result && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Empfängergruppen
                </div>
                <AudiencePresets
                  accent="purple"
                  activeSet={audiences}
                  onApply={(cohorts) => setAudiences(new Set(cohorts))}
                />
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "leads",             label: "Leads",        hint: "Stufe-2 Interessenten" },
                    { key: "customers",         label: "Kunden",       hint: "zahlende Accounts" },
                    { key: "contacts",          label: "Kontakte",     hint: "Kontaktformular" },
                    { key: "partners",          label: "Partner",      hint: "approved Affiliates" },
                    { key: "past_attendees",    label: "Teilnehmer",   hint: "haben an vergangenem Webinar teilgenommen" },
                    { key: "past_registrants",  label: "Registranten", hint: "haben sich für vergangenes Webinar angemeldet (Teilnehmer + No-Shows)" },
                  ].map(({ key, label, hint }) => {
                    const active = audiences.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleAudience(key)}
                        data-testid={`broadcast-audience-${key}`}
                        disabled={busy}
                        className={`text-left rounded-xl border px-3 py-2 transition-all ${
                          active
                            ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex h-4 w-4 items-center justify-center rounded border-2 ${
                              active ? "border-purple-600 bg-purple-600" : "border-slate-300 bg-white"
                            }`}
                          >
                            {active && <span className="text-white text-[10px] leading-none">✓</span>}
                          </span>
                          <span className="font-display font-semibold text-sm text-slate-900">{label}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 ml-6 leading-tight">{hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {dryRun && !result && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
                <div className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Vorschau
                </div>
                <p className="text-slate-600 mb-1">
                  <strong className="text-2xl font-mono-tab block text-[var(--al-purple)] mb-1">{dryRun.candidates_count}</strong>
                  Empfänger erhalten die Einladung
                  {dryRun.skipped_already_invited > 0 && !force && (
                    <span className="block mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      ⚠️ <strong>{dryRun.skipped_already_invited}</strong> Empfänger werden übersprungen, weil sie bereits eine andere Webinar-Einladung erhalten haben.
                    </span>
                  )}
                </p>
                {dryRun.recipients && dryRun.recipients.length > 0 && (
                  <RecipientsList recipients={dryRun.recipients} />
                )}
                <label className="mt-4 flex items-start gap-2.5 cursor-pointer p-3 rounded-lg hover:bg-amber-50/50 border border-transparent hover:border-amber-200 transition">
                  <input
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                    data-testid="webinar-broadcast-force"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-xs leading-relaxed text-slate-700">
                    <strong className="text-amber-700">Force-Modus:</strong> Auch an Empfänger senden, die bereits eine andere Webinar-Einladung erhalten haben. <span className="text-slate-500">Nur für neue, eigenständige Webinar-Events nutzen.</span>
                  </span>
                </label>
              </div>
            )}

            {result && (
              <div className={`rounded-xl p-4 text-sm border ${result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
                {result.ok ? (
                  <>
                    ✓ <strong>{result.sent}</strong> Mails verschickt
                    {result.failed > 0 && <>, <strong>{result.failed}</strong> Fehler</>}
                    {result.skipped > 0 && <>, <strong>{result.skipped}</strong> übersprungen</>}
                    {result.skipped_already_invited > 0 && <span className="block mt-1 text-xs">({result.skipped_already_invited} hatten bereits andere Webinar-Einladung)</span>}
                    .
                  </>
                ) : (
                  <>Broadcast nicht ausgeführt: {result.reason || "siehe Logs"}</>
                )}
              </div>
            )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap justify-end gap-2 flex-shrink-0 bg-white">
              <button onClick={onClose} className="al-cta-ghost">Schließen</button>
              {!result && dryRun && (
                <button
                  onClick={doSend}
                  disabled={busy || dryRun.candidates_count === 0}
                  data-testid="webinar-broadcast-confirm"
                  className="al-cta disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Jetzt an {dryRun.candidates_count} Empfänger senden
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* ---------- Broadcast Recipients List (full email list) ---------- */
const SOURCE_LABELS = {
  contact_requests: { label: "Kontakt", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  leads: { label: "Lead", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  customer_accounts: { label: "Kunde", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function RecipientsList({ recipients }) {
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? recipients.filter(
        (r) =>
          (r.email || "").toLowerCase().includes(q) ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.partner_slug || "").toLowerCase().includes(q),
      )
    : recipients;

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(
        filtered.map((r) => r.email).join("\n"),
      );
      setCopied(true);
      toast.success(`${filtered.length} E-Mail-Adressen kopiert.`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren fehlgeschlagen — Browser blockiert Zwischenablage.");
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest">
          Alle Empfänger ({filtered.length}{query ? ` / ${recipients.length}` : ""})
        </div>
        <button
          type="button"
          onClick={copyAll}
          data-testid="broadcast-recipients-copy"
          className="al-cta-ghost text-[11px] py-1 px-2"
          title="Alle gefilterten E-Mail-Adressen in die Zwischenablage kopieren"
        >
          <Copy className="h-3 w-3" />
          <span>{copied ? "Kopiert!" : "Alle kopieren"}</span>
        </button>
      </div>
      <div className="relative mb-2">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche nach E-Mail, Name oder Partner-Slug…"
          data-testid="broadcast-recipients-search"
          className="al-input pl-8 text-xs py-1.5"
        />
      </div>
      <div
        className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100"
        data-testid="broadcast-recipients-list"
      >
        {filtered.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-6">
            Keine Treffer.
          </div>
        ) : (
          filtered.map((r, i) => {
            const meta = SOURCE_LABELS[r.source] || {
              label: r.source,
              cls: "bg-slate-50 text-slate-600 border-slate-200",
            };
            return (
              <div
                key={`${r.email}-${i}`}
                className="px-3 py-1.5 flex items-center justify-between gap-2 hover:bg-violet-50/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-slate-800 truncate">
                    {r.email}
                  </div>
                  {(r.name || r.partner_slug) && (
                    <div className="text-[10px] text-slate-500 truncate">
                      {r.name || "—"}
                      {r.partner_slug ? ` · via /${r.partner_slug}` : ""}
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${meta.cls}`}
                >
                  {meta.label}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
